import Big from 'big-js';
import { getContractedVolume, RELEASING_VOLUME_PRODUCT_TYPE, TRADING_VOLUME_PRODUCT_TYPE, volumeBigToNumber } from '../../volumes';
import { countAlreadyBought, formatBuyInfo, getPeriodInfo } from '../countVolumes';

/**
 * @param {Big} askedPercent askedPercent
 * @param {ProductVolumeDef} periodContractedVolume periodContractedVolume
 * @param {Boolean} releasing is trading releasing of product
 * @returns {ProductVolumeDef} Period buying product with quantities
 */
const countBuyingProductProfile = (askedPercent, periodContractedVolume, releasing = false) => {
  const buyingQuantities = periodContractedVolume.quantities.map((monthQuantity) => {
    const buying = new Big(monthQuantity.quantity).mul(askedPercent);
    return {
      month: monthQuantity.month,
      quantity: volumeBigToNumber(buying),
    };
  });
  return {
    productType: releasing ? RELEASING_VOLUME_PRODUCT_TYPE : TRADING_VOLUME_PRODUCT_TYPE,
    quantities: buyingQuantities,
  };
};

/**
 * @param {ProductVolumeDef} buyingProduct Buying product
 * @returns {Big} Sum of bought MWh
 */
const countTotalVolumeBought = (buyingProduct) => {
  let totalVolume = new Big(0);
  buyingProduct.quantities.forEach((mothVolume) => {
    totalVolume = totalVolume.add(mothVolume.quantity);
  });
  return totalVolume;
};

/**
 * @param {Big} askedPercent askedPercent
 * @param {ProductVolumeDef} periodContractedVolume periodContractedVolume
 * @param {number} year year
 * @param {Boolean} releasing is trading releasing of product
 * @returns {PurchaseInfo} Volumes counted by profile, 'BUYING' product type
 */
export const countProfileByPercent = (askedPercent, periodContractedVolume, year, releasing) => {
  const periodInfo = getPeriodInfo(periodContractedVolume, year);
  const buyingProduct = countBuyingProductProfile(askedPercent, periodContractedVolume, releasing);
  const totalBoughtMWh = countTotalVolumeBought(buyingProduct);

  const totalBoughtMW = totalBoughtMWh.div(periodInfo.hoursInPeriod);

  return {
    buyTotalsInfo: formatBuyInfo(askedPercent, totalBoughtMWh, totalBoughtMW),
    buyingProduct: buyingProduct,
  };
};

/**
 * @param {Big} mwhAsking askedPercent
 * @param {ProductVolumeDef} periodContractedVolume periodContractedVolume
 * @param {number} year year
 * @param {Boolean} releasing is trading releasing of product
 * @returns {PurchaseInfo} Volumes counted by profile, 'BUYING' product type
 */
export const countProfileByMWh = (mwhAsking, periodContractedVolume, year, releasing) => {
  const periodInfo = getPeriodInfo(periodContractedVolume, year);
  const percentAsking = mwhAsking.div(periodInfo.totalContractedMWh);

  const buyingProduct = countBuyingProductProfile(percentAsking, periodContractedVolume, releasing);
  const totalBoughtMWh = countTotalVolumeBought(buyingProduct);
  const mwAsking = mwhAsking.div(periodInfo.hoursInPeriod);

  return {
    buyTotalsInfo: formatBuyInfo(percentAsking, totalBoughtMWh, mwAsking),
    buyingProduct: buyingProduct,
  };
};

/**
 * @param {Big} mwAsking askedPercent
 * @param {ProductVolumeDef} periodContractedVolume periodContractedVolume
 * @param {number} year year
 * @param {Boolean} releasing is trading releasing of product
 * @returns {PurchaseInfo} Volumes counted by profile, 'BUYING' product type
 */
export const countProfileByMW = (mwAsking, periodContractedVolume, year, releasing) => {
  const periodInfo = getPeriodInfo(periodContractedVolume, year);
  const mwhAsking = mwAsking.mul(periodInfo.hoursInPeriod);
  const percentAsking = mwhAsking.div(periodInfo.totalContractedMWh);

  const buyingProduct = countBuyingProductProfile(percentAsking, periodContractedVolume, releasing);
  const totalBoughtMWh = countTotalVolumeBought(buyingProduct);
  const totalMw = totalBoughtMWh.div(periodInfo.hoursInPeriod);

  return {
    buyTotalsInfo: formatBuyInfo(percentAsking, totalBoughtMWh, totalMw),
    buyingProduct: buyingProduct,
  };
};

export const countProfileMaxAllowed = (periodContractedBoughtVolume, year, releasing) => {
  const contractedVolume = getContractedVolume(periodContractedBoughtVolume);
  let highestPercentageBought = new Big(0);
  let lowestPercentageBought = new Big(1);
  contractedVolume.quantities.forEach((contractedMonthQuantity) => {
    const month = contractedMonthQuantity.month;
    const contracted = new Big(contractedMonthQuantity.quantity);
    const alreadyBought = countAlreadyBought(periodContractedBoughtVolume, month);

    const percentageBought = alreadyBought.div(contracted);
    if (percentageBought.gt(highestPercentageBought)) {
      highestPercentageBought = percentageBought;
    }

    if (lowestPercentageBought.gt(percentageBought)) {
      lowestPercentageBought = percentageBought;
    }
  });

  return countProfileByPercent(releasing ? lowestPercentageBought : new Big(1).sub(highestPercentageBought), contractedVolume, year, releasing);
};
